import { useEffect, useState } from 'react';

import { Card, CardHeader, DetailList, DetailRow } from '../../components/ui/Card';
import { AsyncBoundary, Skeleton } from '../../components/ui/AsyncBoundary';
import { FleetMap } from '../../components/map/FleetMap';
import { StatTile } from '../../components/charts/StatTile';
import { HealthMeter } from '../../components/ui/Badge';
import { SignalBars } from '../../components/domain/VehicleBits';
import { AttributePanel } from '../../components/domain/AttributePanel';
import { Badge } from '../../components/ui/Badge';
import { useResource } from '../../hooks/useResource';
import { useLiveRoom } from '../../hooks/useLive';
import { healthBand, healthReasons } from '../../lib/domain';
import * as fmt from '../../lib/format';

/**
 * The live view: where the vehicle is now, and what the last packet said.
 *
 * The REST call gives the current state on mount; the socket keeps it current
 * after that. The two are merged into one `reading` so the panels below do not
 * each need to know which source they are reading from.
 */
export function LiveTab({ vehicleId, vehicle }) {
  const latest = useResource(`/api/vehicles/${vehicleId}/telemetry/latest`, null, { pollMs: 30_000 });
  // Labels and units for whatever this device happens to report.
  const catalog = useResource('/api/attributes');
  const [liveReading, setLiveReading] = useState(null);

  useLiveRoom(`vehicle:${vehicleId}`, {
    'telemetry:update': (event) => {
      if (event?.vehicleId === vehicleId) setLiveReading(event.payload);
    },
  });

  // A vehicle change must not leave the previous vehicle's live reading on screen.
  useEffect(() => setLiveReading(null), [vehicleId]);

  return (
    <AsyncBoundary state={latest} skeleton={<Skeleton className="h-96 w-full" />}>
      {(data) => {
        const packet = data.telemetry;
        const state = data.state;

        // Live wins where it has a value; the REST packet fills the rest.
        const reading = {
          time: liveReading?.time ?? packet?.time,
          lat: liveReading?.lat ?? packet?.lat,
          lon: liveReading?.lon ?? packet?.lon,
          positionValid: liveReading?.positionValid ?? packet?.position_valid,
          speedKph: liveReading?.speedKph ?? packet?.speed_kph,
          headingDeg: liveReading?.headingDeg ?? packet?.heading_deg,
          sats: liveReading?.sats ?? packet?.sats,
          hdop: liveReading?.hdop ?? packet?.hdop,
          gsmSignal: liveReading?.gsmSignal ?? packet?.gsm_signal,
          operator: liveReading?.operator ?? packet?.operator,
          tempC: liveReading?.tempC ?? packet?.temp_c,
          humidityPct: liveReading?.humidityPct ?? packet?.humidity_pct,
          memPct: liveReading?.memPct ?? packet?.mem_pct,
          tiltAlert: liveReading?.tiltAlert ?? packet?.tilt_alert,
          healthScore: liveReading?.healthScore ?? packet?.health_score ?? state?.health_score,
        };

        const band = healthBand({ score: reading.healthScore, online: state?.online });
        const reasons = healthReasons(state?.health_flags);

        const mapVehicle = vehicle
          ? {
              ...vehicle,
              last_lat: reading.positionValid ? reading.lat : vehicle.last_lat,
              last_lon: reading.positionValid ? reading.lon : vehicle.last_lon,
              last_speed_kph: reading.speedKph,
              last_heading_deg: reading.headingDeg,
              online: state?.online,
              vehicle_state: state?.vehicle_state,
            }
          : null;

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatTile label="Speed" value={Math.round(reading.speedKph ?? 0)} unit="km/h" icon="arrow" tone="brand" />
              <StatTile
                label="Device health"
                value={reading.healthScore ?? '—'}
                unit={band.label.toLowerCase()}
                icon="chip"
                tone={band.tone === 'good' ? 'good' : band.tone === 'warning' ? 'warning' : 'critical'}
              />
              <StatTile
                label="Satellites"
                value={reading.sats ?? '—'}
                unit={reading.hdop ? `HDOP ${Number(reading.hdop).toFixed(1)}` : ''}
                icon="satellite"
                tone={reading.positionValid ? 'good' : 'warning'}
              />
              <StatTile
                label="Temperature"
                value={reading.tempC !== null && reading.tempC !== undefined ? Number(reading.tempC).toFixed(1) : '—'}
                unit="°C"
                icon="thermometer"
                tone="accent"
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
              <Card className="overflow-hidden">
                <CardHeader
                  title="Current position"
                  subtitle={
                    reading.positionValid
                      ? fmt.coordinates(reading.lat, reading.lon)
                      : state?.last_lat
                        ? `No GNSS fix right now — last fixed ${fmt.relativeTime(state.last_gps_time || state.last_packet_at)}`
                        : 'No GNSS fix, and no earlier position on record'
                  }
                  icon="location"
                  actions={
                    !reading.positionValid && (
                      <Badge tone="warning" size="sm">
                        No fix
                      </Badge>
                    )
                  }
                />
                <div className="p-3">
                  <FleetMap vehicles={mapVehicle ? [mapVehicle] : []} height={380} selectedId={vehicleId} />
                </div>
              </Card>

              <div className="space-y-4">
                <Card>
                  <CardHeader title="Last packet" icon="clock" />
                  <div className="px-4 py-2">
                    <DetailList>
                      <DetailRow label="Received" value={fmt.dateTime(reading.time)} />
                      <DetailRow label="Speed" value={fmt.speed(reading.speedKph)} />
                      <DetailRow
                        label="Heading"
                        value={reading.headingDeg !== null && reading.headingDeg !== undefined
                          ? `${Math.round(reading.headingDeg)}°`
                          : '—'}
                      />
                      {/* With no fix the packet carries 0,0 — showing that as
                          a coordinate would be a lie dressed up as precision.
                          Fall back to the last position the device actually
                          fixed, and say which one this is. */}
                      <DetailRow
                        label={reading.positionValid ? 'Coordinates' : 'Last known position'}
                        value={
                          reading.positionValid
                            ? fmt.coordinates(reading.lat, reading.lon)
                            : fmt.coordinates(state?.last_lat, state?.last_lon)
                        }
                        mono
                      />
                      <DetailRow
                        label="Signal"
                        value={<SignalBars csq={reading.gsmSignal} />}
                      />
                      <DetailRow label="Operator" value={reading.operator || '—'} />
                      <DetailRow label="Humidity" value={fmt.percent(reading.humidityPct, 1)} />
                      <DetailRow label="Device memory" value={fmt.percent(reading.memPct, 1)} />
                    </DetailList>
                  </div>
                </Card>

                <Card>
                  <CardHeader title="Health breakdown" icon="shield" />
                  <div className="space-y-3 p-4">
                    <HealthMeter
                      score={reading.healthScore}
                      tone={band.tone}
                      label={`Overall — ${band.label}`}
                    />
                    {reasons.length ? (
                      <ul className="space-y-1.5 border-t border-line pt-3">
                        {reasons.map((reason) => (
                          <li key={reason.key} className="flex items-center justify-between gap-2">
                            <span className="text-[13px] text-ink-2">{reason.label}</span>
                            <Badge tone={reason.tone} size="sm">
                              {reason.level}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="border-t border-line pt-3 text-[13px] text-ink-3">
                        All seven health signals are within range.
                      </p>
                    )}
                    {reading.tiltAlert && (
                      <p className="flex items-center gap-2 rounded-lg bg-critical-soft px-3 py-2 text-[13px] text-critical-ink">
                        Tamper / tilt reported by the device.
                      </p>
                    )}
                  </div>
                </Card>
              </div>
            </div>

            {/* Whatever this hardware reports beyond the standard packet —
                fuel, door state, reefer temperature — without a screen having
                been written for any of them. */}
            <AttributePanel
              attributes={state?.attributes}
              catalog={catalog.data?.attributes}
              subtitle="Everything this device is currently reporting"
            />
          </div>
        );
      }}
    </AsyncBoundary>
  );
}
